import React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import type { RuleTestResult } from '../services/categorization-rules-service';

interface RuleTestResultsDialogProps {
  open: boolean;
  onClose: () => void;
  results: RuleTestResult[];
  total: number;
}

/**
 * Dialog displaying the results of a categorization rules dry-run test.
 *
 * Args:
 *     open (boolean): Whether the dialog is visible.
 *     onClose (function): Callback to close the dialog.
 *     results (RuleTestResult[]): The matched transactions.
 *     total (number): Total number of matches.
 */
export const RuleTestResultsDialog: React.FC<RuleTestResultsDialogProps> = ({
  open,
  onClose,
  results,
  total,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Test Results ({total} matches)</DialogTitle>
      <DialogContent>
        {results.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            No transactions matched any active rules.
          </Typography>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Rule</TableCell>
                  <TableCell>Category</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((result) => (
                  <TableRow key={`${result.transaction_id}-${result.rule_id}`}>
                    <TableCell>{result.description || '-'}</TableCell>
                    <TableCell align="right">{result.amount}</TableCell>
                    <TableCell>{result.rule_name}</TableCell>
                    <TableCell>{result.category_name}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
